import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import './AudioMessageRecorder.css';
import {
  AudioMessageRecorderProps,
  formatPlaybackTime,
  formatTime,
  getAudioDuration,
  MAX_DURATION_SEC,
} from './AudioMessageRecorderHelpers';

const getExtensionFromMimeType = (mimeType = '') => {
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('webm')) return 'webm';

  return 'mp4';
};

const getSupportedAudioMimeType = () => {
  const mimeTypes = ['audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm'];
  return mimeTypes.find((type) => window.MediaRecorder?.isTypeSupported(type));
};

const AudioMessageRecorder: React.FC<AudioMessageRecorderProps> = ({
  onAudioRecorded,
  setAudioMessageSubmitted,
  audioMessageSubmitted,
}) => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [isRecordingComplete, setIsRecordingComplete] = useState<boolean>(false);
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);

  const [elapsedTimeMs, setElapsedTimeMs] = useState<number>(0);
  const [histogramData, setHistogramData] = useState<number[]>([]);
  const [audioSubmitted, setAudioSubmitted] = useState<boolean>(false); // ✅ Renamed internal state variable
  const [processedAudioUrls, setProcessedAudioUrls] = useState<Set<string>>(new Set());
  const [actualPlaybackTime, setActualPlaybackTime] = useState<number | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // ======================= Get Duration of the audio file ============================
  useEffect(() => {
    if (!audioUrl) {
      setActualPlaybackTime(null); // Reset duration on delete
      return () => {};
    }

    getAudioDuration(audioUrl)
      .then((duration) => {
        setActualPlaybackTime(duration);
      })
      .catch((error) => {
        console.error('❌ Error fetching audio duration:', error);
      });

    return () => {
      // Cleanup: Stop fetching when the component is unmounted or `audioUrl` changes
      setActualPlaybackTime(null);
    };
  }, [audioUrl]);

  // ========================== Generate Histogram ==========================
  const generateHistogram = async (audioSrc: string): Promise<boolean> => {
    try {
      const response = await fetch(audioSrc);
      const audioData = await response.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(audioData);
      const rawData = audioBuffer.getChannelData(0);
      const audioDurationSec = audioBuffer.duration;
      const maxBars = 22;
      const sampleInterval = audioDurationSec <= 22 ? 1 : audioDurationSec / maxBars;
      const sampleCount = Math.min(maxBars, Math.floor(audioDurationSec / sampleInterval));

      const filteredData: number[] = [];
      for (let i = 0; i < sampleCount; i++) {
        const sampleStart = Math.floor((rawData.length / audioDurationSec) * (i * sampleInterval));
        const sampleEnd = Math.floor(
          (rawData.length / audioDurationSec) * ((i + 1) * sampleInterval),
        );
        let sumSquares = 0;
        let count = 0;
        for (let j = sampleStart; j < sampleEnd; j++) {
          if (rawData[j] !== undefined) {
            sumSquares += rawData[j] ** 2;
            count++;
          }
        }
        let rmsAmplitude = count > 0 ? Math.sqrt(sumSquares / count) : 0;
        filteredData.push(rmsAmplitude);
      }

      const maxVal = Math.max(...filteredData) || 0.01;
      const normalizedData = filteredData.map((value) => {
        let scaledValue = (value / maxVal) * 98 + 2;
        return Math.min(100, Math.max(2, scaledValue));
      });

      // ✅ Ensure histogram data is updated before returning
      setHistogramData(normalizedData);
      console.log('✅ Histogram generated:', normalizedData);

      return true; // Indicate success
    } catch (err) {
      console.error('❌ Error generating histogram:', err);
      return false; // Indicate failure
    }
  };

  // ==================== Audio Playback ================================
  const togglePlayAudio = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [audioUrl]);

  // ==================== END OF Audio Playback ================================
  const mimeType = getSupportedAudioMimeType();
  const { startRecording, stopRecording } = useReactMediaRecorder({
    audio: true,
    blobPropertyBag: { type: mimeType },
    mediaRecorderOptions: { mimeType: mimeType },
    onStop: (_blobUrl, blob) => {
      // Always create fresh object URL for the new recording
      const freshUrl = URL.createObjectURL(blob);
      setAudioUrl(freshUrl);
    },
  });

  // ======================= Histogram generation on new audioUrl =======================
  // 🎬 1st useEffect: Generate histogram when playback panel becomes visible
  useEffect(() => {
    if (audioUrl) {
      console.log('🎬 New `audioUrl` detected, generating histogram...');

      generateHistogram(audioUrl)
        .then((success) => {
          if (success) {
            console.log('🎬 Histogram successfully generated.');
          }
        })
        .catch((error) => {
          console.error('❌ Error generating histogram:', error);
        });
    }
  }, [audioUrl]); // ✅ Runs only when `audioUrl` updates

  // // 🎬 2nd useEffect: Wait for histogram data to be available before calling createData
  useEffect(() => {
    if (histogramData.length > 0) {
      console.log('🎬 Running createData with histogram data:', histogramData);
      createData(histogramData);
    }
  }, [histogramData]);
  // ============================= END OF Run `createData` when playback panel becomes visible

  // ============================= Set the interval for playback timer ====================
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isRecording) {
      const startTime = Date.now() - elapsedTimeMs;
      interval = setInterval(() => {
        setElapsedTimeMs(Date.now() - startTime);
      }, 10);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // ======================= TOGGLE RECORDING  =======================
  const toggleRecording = async () => {
    if (!isRecording) {
      if (isRecordingComplete) {
        // Start a new recording
        setHistogramData([]);
        startRecording();
        setIsRecording(true);
        setIsRecordingComplete(false);
        setAudioDuration(0);
        setElapsedTimeMs(0);
        setProgress(0);
      } else {
        // Resume recording
        startRecording();
        setIsRecording(true);
      }
      setIsPanelVisible(true);
      intervalRef.current = setInterval(() => {
        setAudioDuration((prev) => {
          if (prev + 1 >= MAX_DURATION_SEC) {
            stopRecording();
            clearInterval(intervalRef.current);
            setIsRecording(false);
            setIsRecordingComplete(true);
            return MAX_DURATION_SEC;
          }
          return prev + 1;
        });
        setElapsedTimeMs((prev) => prev + 1000);
        setProgress((prev) => Math.min(prev + 100 / MAX_DURATION_SEC, 100));
      }, 1000);
    } else {
      console.log('🛑 Stopping Recording... Waiting for onStop...');
      stopRecording(); // ✅ Stop recording and wait for `onStop` to trigger
    }
  };

  // ======================= Playback Panel Timer =================================
  useEffect(() => {
    if (audioRef.current && audioRef.current.duration) {
      setElapsedTimeMs(audioRef.current.duration * 1000);
    }
  }, [audioUrl]);

  // ======================= Stop Recording Handler =======================
  const stopRecordingAndKeep = () => {
    console.log('🛑 Stopping Recording... Waiting for onStop...');
    stopRecording();
    clearInterval(intervalRef.current);

    // Stop recording & show playback panel on FIRST CLICK
    setIsRecording(false);
    setIsRecordingComplete(true);
    setIsPanelVisible(false);
    setIsPlaying(false);
  };
  // ======================= END OF Stop Recording Handler ============================

  const createData = async (histogramData: number[]) => {
    try {
      // ✅ Fetch the actual duration
      // const actualDuration = await getAudioDuration(audioUrl);

      // Fetch the latest recorded audio blob
      const latestAudioUrl = audioUrl;
      if (!audioUrl || processedAudioUrls.has(latestAudioUrl)) {
        console.warn('⚠️ Skipping createData - Already processed or invalid URL.');
        return; // ✅ Prevent redundant processing
      }

      // console.log("📥 Fetching latest audio blob:", latestAudioUrl);

      // ✅ Fetch the newly recorded audio blob and create a fresh unique blob URL
      const response = await fetch(latestAudioUrl);
      const audioBlob = await response.blob();
      const uniqueBlobUrl = URL.createObjectURL(audioBlob);

      // ✅ Fetch actual duration of the new audio file
      const actualDuration = await getAudioDuration(uniqueBlobUrl);

      // Generate new unique file object and object URL
      const actualMimeType = audioBlob.type;
      const fileExtension = getExtensionFromMimeType(actualMimeType);

      const fileName = `audio_${Date.now()}.${fileExtension}`;
      const file = new File([audioBlob as any], fileName, { type: actualMimeType });
      const fileDownloadUrl = URL.createObjectURL(file);

      // ✅ Properly format histogram data
      const histogramArray = histogramData.length
        ? histogramData.map((value) => `${value}%`)
        : ['0%'];

      // console.log("📊 Processed Histogram Data:", histogramArray);

      // ✅ Create flags for an audio message
      const audioFlags = {
        isSimpleText: false,
        isURL: false,
        isImageSingle: false,
        isImageGallery: false,
        isAudio: true,
        isFile: true, // Now includes a downloadable file
        isVideo: false,
        isHTML: false,
      };

      const blobToDataURL = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });

      // ✅ Build JSON data for the audio message
      const jsonData = {
        file: fileDownloadUrl, // ✅ Downloadable MP4 link
        audioBlobUrl: uniqueBlobUrl, // ✅ Unique blob URL for playback
        playbackTime: actualDuration.toString(), // ✅ Accurate playback time,
        histogram: histogramArray,
        audioDataUrl: await blobToDataURL(audioBlob),
      };

      console.log('RESULT OF DATA PREPARATION FUNC');
      console.log('📂 File Name:', fileName);
      console.log('📂 File Download Link:', jsonData.file);
      console.log('🔗 Audio Blob URL:', jsonData.audioBlobUrl);
      console.log('⏳ Playback Time:', jsonData.playbackTime);
      console.log('📊 Histogram:', jsonData.histogram);
      console.log('END OF DATA PREPARATION FUNC');

      // ✅ Pass the audio data upward via `onAudioRecorded`
      if (typeof onAudioRecorded === 'function') {
        onAudioRecorded({ flags: audioFlags, jsonData });
      }

      // ✅ Mark the audio URL as processed to prevent infinite loops
      setProcessedAudioUrls((prevSet) => new Set(prevSet).add(audioUrl));
    } catch (error) {
      console.error('❌ Error in createData:', error);
    }
  };

  // ======================= Delete what was recorded =======================
  const deleteRecording = () => {
    console.log('🚮 Deleting Recording...');
    stopRecording();
    clearInterval(intervalRef.current);

    // clearBlobUrl();
    setIsRecording(false);
    setIsRecordingComplete(false);
    setAudioDuration(0);
    setElapsedTimeMs(0);
    setProgress(0);
    setIsPanelVisible(false);
    setHistogramData([]);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
      audioRef.current = null; // ✅ Reset ref
    }
    setActualPlaybackTime(null); // ✅ Ensure playback time is reset
    setTimeout(() => {
      setAudioUrl(null);
    }, 10);

    console.log('🗑 Recording deleted successfully.');
  };

  // ======================= Skip playback panel on submit =======================
  const skipAudioPlaybackPanelOnSubmit = () => {
    stopRecording();
    clearInterval(intervalRef.current);

    setIsRecording(false);
    setIsRecordingComplete(false);
    setAudioDuration(0);
    setElapsedTimeMs(0);
    setProgress(0);
    setIsPanelVisible(false);
    setHistogramData([]);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
      audioRef.current = null; // ✅ Reset ref
    }

    setActualPlaybackTime(null); // ✅ Ensure playback time is reset
    setTimeout(() => {
      setAudioUrl(null);
    }, 10);

    console.log('Audio Panel is skipped after form submission');
  };

  useEffect(() => {
    if (audioMessageSubmitted) {
      console.log('🎬 Audio message submitted, skipping playback panel...');

      skipAudioPlaybackPanelOnSubmit();

      // ✅ Reset both local and parent states
      setAudioSubmitted(false);
      setAudioMessageSubmitted(false);
    }
  }, [audioMessageSubmitted]); // ✅ Watching parent state

  // ======================= Playback Progress with full duration before play =================
  useEffect(() => {
    if (!audioRef.current) return () => {};

    const updateProgress = () => {
      if (!audioRef.current) return;

      // ✅ Update the playback progress
      setElapsedTimeMs(Math.floor(audioRef.current.currentTime * 1000));
      setProgress((audioRef.current.currentTime / actualPlaybackTime) * 100);
    };

    const handleAudioEnd = () => {
      setIsPlaying(false);
      setElapsedTimeMs(0);
      setProgress(0);
    };

    if (isPlaying) {
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('ended', handleAudioEnd);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [isPlaying, actualPlaybackTime]); // ✅ Watch actualPlaybackTime

  return (
    <div className="audioRecorder">
      <button className="audio-message-button" onClick={toggleRecording}></button>
      <AnimatePresence>
        {isPanelVisible && (
          <motion.div
            className="audio-recorder-container"
            initial={{ opacity: 0, scale: 0.8, transformOrigin: '3% 50%' }}
            animate={{ opacity: 1, scale: 1, transformOrigin: '3% 50%' }}
            exit={{ opacity: 0, scale: 0.8, transformOrigin: '3% 50%' }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.2 }}
          >
            <div className="recording-panel">
              <div className="progress-bar">
                <div className="progress" style={{ width: `${progress}%` }}></div>
                <div className="inair-icon"></div>
                <span className="recording-timer">{formatTime(elapsedTimeMs)}</span>
              </div>
              <div className="pulse">
                <span style={{ '--i': 0 } as React.CSSProperties}></span>
                <span style={{ '--i': 1 } as React.CSSProperties}></span>
                <span style={{ '--i': 2 } as React.CSSProperties}></span>
                <span style={{ '--i': 3 } as React.CSSProperties}></span>
              </div>
              <div className="audio-controls">
                <button onClick={stopRecordingAndKeep} className="stop-button"></button>
                <button onClick={deleteRecording} className="close-audio-button"></button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!isPanelVisible && isRecordingComplete && (
        <motion.div
          className="audio-playback-container"
          initial={{ opacity: 0, scale: 0.8, transformOrigin: '3% 50%' }}
          animate={{ opacity: 1, scale: 1, transformOrigin: '3% 50%' }}
          exit={{ opacity: 0, scale: 0.8, transformOrigin: '3% 50%' }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.2 }}
        >
          <div className="audio-playback">
            <button
              onClick={togglePlayAudio}
              className={`play-button ${isPlaying ? 'stop' : 'play'}`}
            ></button>
            <audio ref={audioRef} src={audioUrl}></audio>
            <div
              className="histogram-container"
              style={{ width: `${histogramData.length * 2 + 3 * (histogramData.length - 1)}px` }}
            >
              <div className="histogram-progress" style={{ width: `${progress}%` }}></div>
              {histogramData.map((value, index) => (
                <React.Fragment key={index}>
                  <div className="bar-col-wrap">
                    <div className="col-cap" style={{ height: `${100 - value}%` }}></div>
                    <div className="histogram-bar" style={{ height: `${value}%` }}></div>
                  </div>
                  <div className="bar-gap"></div>
                </React.Fragment>
              ))}
            </div>
            <span className="playback-timer">
              {isPlaying || elapsedTimeMs > 0
                ? formatPlaybackTime(elapsedTimeMs)
                : formatPlaybackTime((actualPlaybackTime || 0) * 1000)}
            </span>
          </div>
          <button onClick={deleteRecording} className="delete-button"></button>
        </motion.div>
      )}
    </div>
  );
};

export default AudioMessageRecorder;
